<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch, inject } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '../stores/auth'
import { useI18n } from 'vue-i18n'
import api from '../services/api'
import { notificationService } from '../services/notification.service'
import conversationService from '../services/conversation.service'
import { getEcho } from '../services/echo'
import DemoRequestModal from './DemoRequestModal.vue'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const { t, locale } = useI18n()
const isKeyboardOpen = inject('isKeyboardOpen', ref(false))

// Check if user needs profile setup (no role, no permissions, no club)
const needsProfileSetup = computed(() => {
  const currentRole = authStore.initData?.persons?.current?.role
  const permissions = authStore.initData?.permission_model || []
  const clubUuid = authStore.initData?.club?.uuid

  return authStore.isAuthenticated && !currentRole && permissions.length === 0 && !clubUuid
})

// Unread notifications count
const unreadNotificationsCount = ref(0)

// Unread conversations count and UUIDs
const unreadConversationsCount = ref(0)
const unreadConversationUuids = ref<string[]>([])

const isDemoModalOpen = ref(false)
const isMobileAppModalOpen = ref(false)

const appStoreUrl = computed(() => authStore.initData?.app_store_url || '')
const googleShopUrl = computed(() => authStore.initData?.google_shop_url || '')
const hasMobileAppLinks = computed(() => !!(appStoreUrl.value || googleShopUrl.value))

const openMobileAppModal = () => {
  profileDropdownOpen.value = false
  isMobileAppModalOpen.value = true
}

watch(
  () => route.query.demo,
  (val) => {
    if (val === '1' && !authStore.isAuthenticated) {
      isDemoModalOpen.value = true
      const { demo, ...rest } = route.query
      router.replace({ query: rest })
    }
  },
  { immediate: true }
)

// Dropdown states
const profileDropdownOpen = ref(false)
const overflowMenuOpen = ref(false)
const roleDropdownOpen = ref(false)
const setupProfileDropdownOpen = ref(false)

// Get all available persons/roles from init data
const availableRoles = computed(() => {
  return authStore.initData?.persons?.all || []
})

// Get current person/role
const currentRole = computed(() => {
  return authStore.initData?.persons?.current || null
})

// Get current role display info
const currentRoleDisplay = computed(() => {
  if (!currentRole.value) return t('profile.selectRole')

  const roleText = getRoleText(currentRole.value.role)
  const firstName = currentRole.value.first_name || ''
  const lastName = currentRole.value.last_name || ''
  const fullName = `${firstName} ${lastName}`.trim()

  return fullName ? `${fullName} (${roleText})` : roleText
})

// Get subscription expiry date
const subscriptionExpiryDate = computed(() => {
  const dateTo = authStore.initData?.subscriptions?.date_to
  if (!dateTo) return t('navigation.noData')

  const date = new Date(dateTo)
  const loc = ({ pl: 'pl-PL', en: 'en-US', fr: 'fr-FR', es: 'es-ES', de: 'de-DE', it: 'it-IT', pt: 'pt-PT', cs: 'cs-CZ', nl: 'nl-NL', sv: 'sv-SE', da: 'da-DK', hu: 'hu-HU', ro: 'ro-RO', no: 'nb-NO', lt: 'lt-LT', hr: 'hr-HR', fi: 'fi-FI' }[locale.value] || 'en-US')
  return date.toLocaleDateString(loc, { year: 'numeric', month: 'long', day: 'numeric' })
})

// Get club UUID for navigation
const clubUuid = computed(() => authStore.initData?.club?.uuid || null)

// Get club logo URL
const clubLogoUrl = computed(() => authStore.initData?.club?.logo_url || null)

// Get person display name
const getPersonDisplayName = (person: any) => {
  const roleText = getRoleText(person.role)
  const firstName = person.first_name || ''
  const lastName = person.last_name || ''
  const fullName = `${firstName} ${lastName}`.trim()

  return fullName ? `${fullName} (${roleText})` : roleText
}

// Check permissions
const hasPermission = (permission: string) => {
  const permissions = authStore.initData?.permission_model || []
  return permissions.includes(permission)
}

// Toggle functions
const toggleProfileDropdown = () => {
  profileDropdownOpen.value = !profileDropdownOpen.value
  overflowMenuOpen.value = false
  roleDropdownOpen.value = false
}

const toggleOverflowMenu = () => {
  overflowMenuOpen.value = !overflowMenuOpen.value
  profileDropdownOpen.value = false
  roleDropdownOpen.value = false
}

const toggleRoleDropdown = () => {
  roleDropdownOpen.value = !roleDropdownOpen.value
  // Nie zamykaj profileDropdownOpen - role dropdown jest wewnątrz
  overflowMenuOpen.value = false
}

const toggleSetupProfileDropdown = () => {
  setupProfileDropdownOpen.value = !setupProfileDropdownOpen.value
}

// Change role/person
const changeRole = async (personUuid: string) => {
  if (!authStore.user?.uuid) return

  try {
    roleDropdownOpen.value = false

    // Call API to switch person
    await api.put(`v1/auth/${authStore.user.uuid}/persons/switch`, {
      person_uuid: personUuid
    })

    // Refresh init data
    await authStore.initUserData(authStore.user.uuid)

    // Reload the page to refresh all data
    window.location.reload()
  } catch (err: any) {
    console.error('Error switching role:', err)
    alert(err.response?.data?.message || t('navigation.errorSwitchingRole'))
  }
}

// Close dropdowns when clicking outside
const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as HTMLElement

  // Check if click is outside dropdowns
  if (!target.closest('.profile-dropdown') && !target.closest('.overflow-menu') && !target.closest('.role-selector') && !target.closest('.setup-profile-dropdown')) {
    profileDropdownOpen.value = false
    overflowMenuOpen.value = false
    roleDropdownOpen.value = false
    setupProfileDropdownOpen.value = false
  }
}

// Get role display text
const getRoleText = (role: string) => {
  return t(`roles.${role}`, role)
}

// Watch for language changes from auth store
watch(
  () => authStore.initData?.lang,
  (newLang) => {
    if (newLang && locale.value !== newLang) {
      locale.value = newLang
    }
  },
  { immediate: true }
)

// Handle logout
const handleLogout = async () => {
  await authStore.logout()
  router.push('/login')
}

// Load unread notifications count
const loadUnreadCount = async () => {
  try {
    if (clubUuid.value) {
      unreadNotificationsCount.value = await notificationService.getUnreadCount(clubUuid.value)
    }
  } catch (error) {
    console.error('Error loading unread notifications count:', error)
  }
}

// Load unread conversations count
const loadUnreadConversationsCount = async () => {
  try {
    if (clubUuid.value) {
      const data = await conversationService.getUnreadData(clubUuid.value)
      unreadConversationsCount.value = data.unread_count
      unreadConversationUuids.value = data.conversation_uuids
    }
  } catch (error) {
    console.error('Error loading unread conversations count:', error)
  }
}

// Handle conversation marked as read event
const handleConversationMarkedAsRead = () => {
  loadUnreadConversationsCount()
}

// Subscribe to Echo private channel for real-time updates
const currentPersonUuid = computed(() => authStore.userInitData?.persons?.current?.uuid || null)
let echoChannel: any = null

const subscribeToEcho = () => {
  const echo = getEcho()
  if (!echo || !currentPersonUuid.value) return

  echoChannel = echo.private(`person.${currentPersonUuid.value}`)
    .listen('.notification.created', () => {
      unreadNotificationsCount.value += 1
    })
    .listen('.notification.read', (data: { count: number }) => {
      unreadNotificationsCount.value = Math.max(0, unreadNotificationsCount.value - (data.count || 1))
    })
    .listen('.message.sent', (data: any) => {
      // Update nav counter
      if (data.conversationUuid && !unreadConversationUuids.value.includes(data.conversationUuid)) {
        unreadConversationUuids.value.push(data.conversationUuid)
        unreadConversationsCount.value += 1
      }

      // Forward to conversation views via custom event
      window.dispatchEvent(new CustomEvent('echoMessageSent', { detail: data }))
    })
}

const unsubscribeFromEcho = () => {
  const echo = getEcho()
  if (echo && currentPersonUuid.value) {
    echo.leave(`person.${currentPersonUuid.value}`)
  }
  echoChannel = null
}

// Lifecycle hooks
onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  window.addEventListener('conversationMarkedAsRead', handleConversationMarkedAsRead)
  loadUnreadCount()
  loadUnreadConversationsCount()
  subscribeToEcho()

  // Refresh count every 60 seconds (fallback)
  const intervalId = setInterval(() => {
    loadUnreadCount()
    loadUnreadConversationsCount()
  }, 60000)

  onBeforeUnmount(() => {
    clearInterval(intervalId)
  })
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
  window.removeEventListener('conversationMarkedAsRead', handleConversationMarkedAsRead)
  unsubscribeFromEcho()
})

// Watch for club changes
watch(clubUuid, () => {
  loadUnreadCount()
  loadUnreadConversationsCount()
})

// Watch for person UUID changes (subscribe to Echo when it becomes available)
watch(currentPersonUuid, (newVal, oldVal) => {
  if (newVal && newVal !== oldVal) {
    if (oldVal) {
      unsubscribeFromEcho()
    }
    subscribeToEcho()
  }
})
</script>

<template>
  <!-- Guest Navigation (Not Logged In) -->
  <nav v-if="!authStore.isAuthenticated" class="bg-white border-b-2 border-gray-300 shadow-sm" style="padding-top: var(--safe-area-top)">
    <div class="max-w-7xl mx-auto px-4">
      <div class="flex justify-between items-center h-16">
        <div class="flex items-center">
          <router-link to="/" class="flex items-center">
            <img src="/sportovo_logo_dark.png" alt="sportovo.io" class="h-8">
          </router-link>
        </div>
        <div class="flex items-center gap-3">
          <router-link to="/login" class="text-sm text-gray-600 hover:text-gray-900">{{ t('navigation.login') }}</router-link>
          <router-link to="/register" class="py-2 px-4 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700">
            {{ t('navigation.register') }}
          </router-link>
        </div>
      </div>
    </div>
  </nav>

  <!-- Profile Setup Navigation (Logged In but needs profile setup) -->
  <nav v-else-if="needsProfileSetup" class="bg-white border-b-2 border-gray-300 shadow-sm" style="padding-top: var(--safe-area-top)">
    <div class="w-full px-4">
      <div class="flex justify-between items-center h-16">
        <div class="flex items-center gap-4">
          <div v-if="route.name !== 'choose-profile'" class="w-10 h-10 border-2 border-gray-300 rounded flex items-center justify-center bg-white flex-shrink-0">
            <img
              v-if="clubLogoUrl"
              :src="clubLogoUrl"
              :alt="t('club.logo')"
              class="w-full h-full object-contain p-1"
            >
            <div
              v-else
              :class="[
                'text-[8px] text-center leading-tight px-0.5',
                authStore.isDemo ? 'text-red-600 font-semibold' : 'text-gray-400'
              ]"
            >
              {{ authStore.isDemo ? t('club.demoAccount') : t('club.noLogo') }}
            </div>
          </div>
          <img src="/sportovo_logo_dark.png" alt="sportovo.io" class="h-8">
        </div>
        <div class="flex items-center gap-3">
          <router-link
            to="/choose-profile"
            class="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700"
          >
            {{ t('navigation.chooseProfile') }}
          </router-link>
          <div class="relative setup-profile-dropdown">
            <button
              @click.stop="toggleSetupProfileDropdown"
              class="px-4 py-2 bg-gray-100 text-sm font-medium text-gray-700 rounded hover:bg-gray-200 border border-gray-300"
            >
              {{ t('navigation.account') }}
            </button>

            <!-- Dropdown Menu -->
            <div
              v-show="setupProfileDropdownOpen"
              class="absolute right-0 mt-2 w-48 bg-white border-2 border-gray-300 rounded shadow-md z-10"
            >
              <router-link to="/edit-profile" class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                {{ t('profile.editProfile') }}
              </router-link>
              <button @click="handleLogout" class="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100">
                {{ t('profile.logout') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </nav>

  <!-- Authenticated Navigation (Logged In with full access) -->
  <template v-else>
    <!-- Top Navigation -->
    <nav class="bg-white border-b-2 border-gray-300 shadow-sm" style="padding-top: var(--safe-area-top)">
      <div class="w-full px-4">
        <div class="flex justify-between items-center h-16">
          <!-- Left Side: Logo + Menu Items (Desktop) -->
          <div class="flex items-center gap-4">
            <!-- Logo -->
            <div class="w-10 h-10 border-2 border-gray-300 rounded flex items-center justify-center bg-white flex-shrink-0">
              <img
                v-if="clubLogoUrl"
                :src="clubLogoUrl"
                :alt="t('club.logo')"
                class="w-full h-full object-contain p-1"
              >
              <div
                v-else
                :class="[
                  'text-[8px] text-center leading-tight px-0.5',
                  authStore.isDemo ? 'text-red-600 font-semibold' : 'text-gray-400'
                ]"
              >
                {{ authStore.isDemo ? t('club.demoAccount') : t('club.noLogo') }}
              </div>
            </div>

            <!-- Desktop Menu Items (hidden on mobile) -->
            <div class="hidden md:flex items-center gap-1">
              <router-link v-if="currentRole?.role !== 'PLAYER'" to="/dashboard" class="px-3 py-2 text-sm font-medium text-gray-900 hover:bg-gray-100 rounded whitespace-nowrap">
                {{ t('navigation.dashboard') }}
              </router-link>
              <router-link v-if="currentRole && currentRole.role === 'PLAYER' && currentRole.player && currentRole.player.uuid" :to="`/players/${currentRole.player.uuid}`" class="px-3 py-2 text-sm font-medium text-gray-900 hover:bg-gray-100 rounded whitespace-nowrap">
                {{ t('navigation.playerProfile') }}
              </router-link>
              <router-link to="/events" class="px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded whitespace-nowrap">
                {{ t('navigation.events') }}
              </router-link>
              <router-link v-if="hasPermission('COACH_INDEX')" to="/coaches" class="px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded whitespace-nowrap">
                {{ t('navigation.coaches') }}
              </router-link>
              <router-link v-if="hasPermission('CATEGORY_INDEX')" to="/categories" class="px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded whitespace-nowrap">
                {{ t('navigation.categories') }}
              </router-link>

              <!-- Progressive hiding: Płatności hides first below 1150px -->
              <router-link v-if="hasPermission('PAYMENT_INDEX')" to="/payments" class="hidden min-[1150px]:flex px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded whitespace-nowrap">
                {{ t('navigation.payments') }}
              </router-link>

              <!-- Progressive hiding: Klub hides third below 950px -->
              <router-link v-if="clubUuid" :to="`/club/${clubUuid}`" class="hidden min-[950px]:flex px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded whitespace-nowrap">
                {{ t('navigation.club') }}
              </router-link>

              <!-- Three Dots Dropdown - shows when any item is hidden -->
              <div class="relative max-[1149px]:block hidden overflow-menu">
                <button
                  @click.stop="toggleOverflowMenu"
                  class="px-3 py-2 text-xl font-medium text-gray-700 hover:bg-gray-100 rounded"
                >
                  ⋮
                </button>
                <div
                  v-show="overflowMenuOpen"
                  class="absolute left-0 mt-2 w-48 bg-white border-2 border-gray-300 rounded shadow-md z-10"
                >
                  <!-- Płatności shows in dropdown below 1150px -->
                  <router-link v-if="hasPermission('PAYMENT_INDEX')" to="/payments" class="max-[1149px]:block hidden px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                    {{ t('navigation.payments') }}
                  </router-link>
                  <!-- Klub shows in dropdown below 950px -->
                  <router-link v-if="clubUuid" :to="`/club/${clubUuid}`" class="max-[949px]:block hidden px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                    {{ t('navigation.club') }}
                  </router-link>
                </div>
              </div>
            </div>
          </div>

          <!-- Right Side: Conversations + Notifications + Selector + Profile Dropdown -->
          <div class="flex items-center gap-3">
            <!-- Conversations Icon -->
            <router-link to="/conversations" class="relative">
              <button class="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 border border-gray-300 cursor-pointer">
                <svg class="w-5 h-5 text-gray-700" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"/>
                  <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"/>
                </svg>
                <!-- Conversations Badge -->
                <span v-if="unreadConversationsCount > 0" class="absolute top-0 right-0 w-5 h-5 bg-red-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                  {{ unreadConversationsCount > 99 ? '99+' : unreadConversationsCount }}
                </span>
              </button>
            </router-link>

            <!-- Notifications Icon -->
            <router-link to="/notifications" class="relative">
              <button class="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 border border-gray-300 cursor-pointer">
                <svg class="w-5 h-5 text-gray-700" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z"/>
                </svg>
                <!-- Notification Badge -->
                <span v-if="unreadNotificationsCount > 0" class="absolute top-0 right-0 w-5 h-5 bg-red-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
                  {{ unreadNotificationsCount > 99 ? '99+' : unreadNotificationsCount }}
                </span>
              </button>
            </router-link>

            <!-- Role Selector (hidden below 1024px and when persons is null) -->
            <div v-if="authStore.initData?.persons" class="hidden lg:block relative role-selector">
              <button
                @click.stop="toggleRoleDropdown"
                class="px-3 py-2 text-sm border border-gray-300 rounded bg-white text-gray-700 hover:bg-gray-50 focus:outline-none focus:border-blue-600 max-w-[250px] flex items-center gap-2"
              >
                <span class="truncate">{{ currentRoleDisplay }}</span>
                <svg class="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd"/>
                </svg>
              </button>

              <!-- Role Dropdown -->
              <div
                v-show="roleDropdownOpen"
                class="absolute right-0 mt-2 w-64 bg-white border-2 border-gray-300 rounded shadow-md z-10 max-h-80 overflow-y-auto"
              >
                <button
                  v-for="person in availableRoles"
                  :key="person.uuid"
                  @click="changeRole(person.uuid)"
                  :class="[
                    'w-full text-left px-4 py-2 text-sm hover:bg-gray-100',
                    person.uuid === currentRole?.uuid ? 'bg-blue-50 font-semibold text-blue-700' : 'text-gray-700'
                  ]"
                >
                  <div class="font-medium">{{ getPersonDisplayName(person) }}</div>
                  <div class="text-xs text-gray-600">{{ person.club?.short_name || person.club?.name || t('common.club') }}</div>
                </button>
              </div>
            </div>

            <!-- Profile Dropdown -->
            <div class="relative profile-dropdown">
              <button
                @click.stop="toggleProfileDropdown"
                class="px-4 py-2 bg-gray-100 text-sm font-medium text-gray-700 rounded hover:bg-gray-200 border border-gray-300"
              >
                {{ t('navigation.account') }}
              </button>

              <!-- Dropdown Menu -->
              <div
                v-show="profileDropdownOpen"
                class="absolute right-0 mt-2 w-48 bg-white border-2 border-gray-300 rounded shadow-md z-10"
              >
                <!-- User Info Section (visible below 1024px) -->
                <div class="lg:hidden px-4 py-3 border-b border-gray-200">
                  <p class="text-xs font-semibold text-gray-900">{{ authStore.user?.email || t('common.user') }}</p>
                  <p v-if="authStore.initData?.persons" class="text-[10px] text-gray-600 mt-0.5">{{ currentRole?.club?.short_name || currentRole?.club?.name || t('common.club') }}</p>
                  <p v-if="authStore.initData?.persons" class="text-[10px] text-gray-600">{{ currentRole ? getRoleText(currentRole.role) : t('common.role') }}</p>

                  <!-- Role selector for mobile -->
                  <div v-if="authStore.initData?.persons && availableRoles.length > 1" class="mt-2 relative role-selector">
                    <button
                      @click.stop="toggleRoleDropdown"
                      class="w-full px-2 py-1 bg-gray-100 text-[10px] font-medium text-gray-700 rounded hover:bg-gray-200 border border-gray-300 flex items-center justify-between"
                    >
                      <span>{{ t('profile.changeRole') }}</span>
                      <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd"/>
                      </svg>
                    </button>

                    <!-- Mobile Role Dropdown -->
                    <div
                      v-show="roleDropdownOpen"
                      class="absolute left-0 right-0 mt-1 bg-white border-2 border-gray-300 rounded shadow-md z-20 max-h-60 overflow-y-auto"
                    >
                      <button
                        v-for="person in availableRoles"
                        :key="person.uuid"
                        @click="changeRole(person.uuid)"
                        :class="[
                          'w-full text-left px-3 py-2 text-xs hover:bg-gray-100',
                          person.uuid === currentRole?.uuid ? 'bg-blue-50 font-semibold text-blue-700' : 'text-gray-700'
                        ]"
                      >
                        <div class="font-medium">{{ getPersonDisplayName(person) }}</div>
                        <div class="text-[10px] text-gray-600">{{ person.club?.short_name || person.club?.name || t('common.club') }}</div>
                      </button>
                    </div>
                  </div>
                </div>

                <router-link to="/edit-profile" class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                  {{ t('profile.editProfile') }}
                </router-link>
                <!-- Klub link visible only on mobile -->
                <router-link v-if="clubUuid" :to="`/club/${clubUuid}`" class="block md:hidden px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                  {{ t('navigation.club') }}
                </router-link>
                <router-link v-if="currentRole?.role === 'OWNER' || currentRole?.role === 'SUPERVISOR'" to="/payment-access" class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                  {{ t('profile.accountPayments') }}
                </router-link>
                <div v-if="hasMobileAppLinks" class="border-t border-gray-200"></div>
                <button v-if="hasMobileAppLinks" @click="openMobileAppModal" class="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 cursor-pointer">
                  {{ t('profile.downloadApp') }}
                </button>
                <div v-if="currentRole?.role === 'OWNER' || currentRole?.role === 'SUPERVISOR'" class="border-t border-gray-200"></div>

                <!-- Subscription Info - tylko dla OWNER i SUPERVISOR -->
                <div v-if="currentRole?.role === 'OWNER' || currentRole?.role === 'SUPERVISOR'" class="px-4 py-3">
                  <p class="text-xs text-gray-700 mb-2">{{ t('profile.accessValidUntil') }} <br/><span class="font-semibold">{{ subscriptionExpiryDate }}</span></p>
                  <router-link to="/payment-access" class="block w-full px-2 py-1.5 bg-green-600 text-xs font-medium text-white rounded hover:bg-green-700 text-center">
                    {{ t('profile.payAccess') }}
                  </router-link>
                </div>

                <div class="border-t border-gray-200"></div>
                <button @click="handleLogout" class="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100">
                  {{ t('profile.logout') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </nav>

    <!-- Bottom Navigation (Mobile Only - Fixed, hidden when keyboard open) -->
    <nav v-show="!isKeyboardOpen" class="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t-2 border-gray-300 shadow-sm z-50" style="padding-bottom: var(--safe-area-bottom)">
      <div class="flex justify-around items-center h-16 sm:h-20">
        <!-- Kokpit -->
        <router-link v-if="currentRole?.role !== 'PLAYER'" to="/dashboard" class="flex flex-col items-center justify-center flex-1 py-2 text-gray-900 hover:bg-gray-100">
          <span class="text-lg sm:text-xl">📊</span>
          <span class="text-[10px] sm:text-xs mt-1">{{ t('navigation.dashboard') }}</span>
        </router-link>

        <!-- Profil zawodnika (tylko dla PLAYER) -->
        <router-link v-if="currentRole && currentRole.role === 'PLAYER' && currentRole.player && currentRole.player.uuid" :to="`/players/${currentRole.player.uuid}`" class="flex flex-col items-center justify-center flex-1 py-2 text-gray-900 hover:bg-gray-100">
          <span class="text-lg sm:text-xl">👤</span>
          <span class="text-[10px] sm:text-xs mt-1">{{ t('navigation.playerProfile') }}</span>
        </router-link>

        <!-- Wydarzenia -->
        <router-link to="/events" class="flex flex-col items-center justify-center flex-1 py-2 text-gray-700 hover:bg-gray-100">
          <span class="text-lg sm:text-xl">📅</span>
          <span class="text-[10px] sm:text-xs mt-1">{{ t('navigation.events') }}</span>
        </router-link>

        <!-- Trenerzy -->
        <router-link v-if="hasPermission('COACH_INDEX')" to="/coaches" class="flex flex-col items-center justify-center flex-1 py-2 text-gray-700 hover:bg-gray-100">
          <span class="text-lg sm:text-xl">👤</span>
          <span class="text-[10px] sm:text-xs mt-1">{{ t('navigation.coaches') }}</span>
        </router-link>

        <!-- Kategorie -->
        <router-link v-if="hasPermission('CATEGORY_INDEX')" to="/categories" class="flex flex-col items-center justify-center flex-1 py-2 text-gray-700 hover:bg-gray-100">
          <span class="text-lg sm:text-xl">📋</span>
          <span class="text-[10px] sm:text-xs mt-1">{{ t('navigation.categories') }}</span>
        </router-link>

        <!-- Płatności -->
        <router-link v-if="hasPermission('PAYMENT_INDEX')" to="/payments" class="flex flex-col items-center justify-center flex-1 py-2 text-gray-700 hover:bg-gray-100">
          <span class="text-lg sm:text-xl">💳</span>
          <span class="text-[10px] sm:text-xs mt-1">{{ t('navigation.payments') }}</span>
        </router-link>
      </div>
    </nav>
  </template>

  <DemoRequestModal v-model="isDemoModalOpen" />

  <!-- Mobile App Modal -->
  <Teleport to="body">
    <div v-if="isMobileAppModalOpen" class="fixed inset-0 z-[200] flex items-center justify-center p-4" @click.self="isMobileAppModalOpen = false">
      <div class="absolute inset-0 bg-black/60" @click="isMobileAppModalOpen = false"></div>
      <div class="relative bg-white rounded-2xl p-8 max-w-md w-full text-gray-900 shadow-2xl">
        <button @click="isMobileAppModalOpen = false" class="absolute top-4 right-4 text-gray-400 hover:text-gray-700 text-xl leading-none">&times;</button>
        <span class="inline-block mb-4 px-3 py-1 rounded-full bg-blue-100 text-xs font-semibold tracking-widest uppercase text-blue-700">{{ t('profile.mobileApps') }}</span>
        <h2 class="text-2xl font-bold mb-3">{{ t('profile.mobileAppModalTitle') }}</h2>
        <p class="text-gray-500 text-sm mb-7">{{ t('profile.mobileAppModalDesc') }}</p>
        <div class="flex gap-4 flex-wrap">
          <a v-if="appStoreUrl" :href="appStoreUrl" target="_blank" rel="noopener noreferrer"
            class="flex items-center gap-3 bg-gray-900 hover:bg-gray-700 border border-gray-800 rounded-xl px-5 py-3 transition-colors text-white">
            <svg class="w-7 h-7 shrink-0" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/></svg>
            <div>
              <div class="text-[10px] text-gray-300">{{ t('profile.downloadFrom') }}</div>
              <div class="text-sm font-semibold">App Store</div>
            </div>
          </a>
          <a v-if="googleShopUrl" :href="googleShopUrl" target="_blank" rel="noopener noreferrer"
            class="flex items-center gap-3 bg-gray-900 hover:bg-gray-700 border border-gray-800 rounded-xl px-5 py-3 transition-colors text-white">
            <svg class="w-7 h-7 shrink-0" viewBox="0 0 24 24" fill="currentColor"><path d="M3.18 23.76c.3.16.64.19.96.09l.06-.03 11.29-6.52-2.5-2.5-9.81 8.96zM.49 2.1C.18 2.44 0 2.95 0 3.61v16.78c0 .66.18 1.17.49 1.51l.08.08 9.4-9.4v-.22L.57 2.02l-.08.08zM20.13 9.35L17.37 7.8 14.6 10.57l2.77 2.77 2.77-1.56c.79-.45.79-1.97-.01-2.43zM3.18.24L14.47 6.76l-2.5 2.5L2.2.3l.08-.06c.27-.17.59-.2.9-.04V.24z"/></svg>
            <div>
              <div class="text-[10px] text-gray-300">{{ t('profile.downloadFrom') }}</div>
              <div class="text-sm font-semibold">Google Play</div>
            </div>
          </a>
        </div>
      </div>
    </div>
  </Teleport>
</template>
